import { EmailStyleSettings } from "../style-settings";
import { EmailTopLevelNode, TiptapEmailJSON } from "../types/tiptap-json";

import { renderEmail } from "@/api/email-render";

export async function renderEmailHtml(
  doc: TiptapEmailJSON,
  settings?: Partial<EmailStyleSettings>,
  variables?: Record<string, string>,
): Promise<string> {
  return renderEmail({ doc, settings, variables });
}

/**
 * Render a Tiptap email document to lightweight, Gmail-style HTML for
 * conversational emails (reply / forward / 1:1 compose) — no marketing
 * scaffold, so it renders consistently across clients. Blasts use
 * `renderEmailHtml` instead.
 */
export async function renderConversationalHtml(
  doc: TiptapEmailJSON,
  variables?: Record<string, string>,
): Promise<string> {
  const html = await renderEmail({ doc, variables, variant: "conversational" });
  // react-email's render() prepends an XHTML doctype; strip it so the body is a
  // clean fragment like a native Gmail reply (clients discard the doctype, but
  // we keep the bytes minimal and the structure identical to Gmail's).
  return html.replace(/^\s*<!DOCTYPE[^>]*>/i, "").trim();
}

export async function renderEmailText(
  doc: TiptapEmailJSON,
  variables?: Record<string, string>,
): Promise<string> {
  // Filter out quote nodes — composers build quoted text separately.
  // Quotes can be inside row > cell, so we filter at the cell content level.
  const filteredContent = doc.content
    .map((node): EmailTopLevelNode | null => {
      if (node.type === "emailRow") {
        const filteredCells = node.content.map((cell) => ({
          ...cell,
          content: cell.content.filter((c) => c.type !== "emailQuote"),
        }));
        // Drop the row entirely if all cells are now empty
        const hasContent = filteredCells.some((c) => c.content.length > 0);
        if (!hasContent) return null;
        return { ...node, content: filteredCells };
      }
      return node;
    })
    .filter((n): n is EmailTopLevelNode => n !== null);

  const filteredDoc: TiptapEmailJSON = { ...doc, content: filteredContent };
  return renderEmail({ doc: filteredDoc, variables, plainText: true });
}
